import { FC, memo, useEffect, useRef } from 'react';
import { CellContext, Row, Table } from '@tanstack/react-table';
import { DragIcon } from '../DragIcon';
import { LEVEL_TABULATION_WIDTH_PX } from '../../../tanstacktable';
import { getRowPathIndexes } from './utils';
import { TableRowPositionChangeHandler } from './types';
import { ROW_UNIQUE_KEY_NAME, UUIDType } from '../../../utils/randomUniqueKey';

export interface DragTableRowButtonProps extends CellContext<any, any> {
  onChangePosition?: TableRowPositionChangeHandler;
  crosshairOffset?: number;
}

export const DragTableRowButton: FC<DragTableRowButtonProps> = memo((props) => {
  const { row, table, onChangePosition, crosshairOffset = 47 } = props;
  const ref = useRef<HTMLDivElement>(null);
  const isFiltered = table.getState().columnFilters.length > 0;

  useEffect(() => {
    const rowDepthIndex = row.depth;
    const rowParentId = row.parentId;
    const rowPathIndexes = getRowPathIndexes(row);
    const rowChildrenCount = row.subRows?.length || 0;
    const element = ref.current;
    const tr = element?.closest('tr');
    const crosshairContainer = tr?.closest(
      '.row-drag-target-crosshair-container'
    ) as HTMLDivElement | undefined;
    const crosshair = crosshairContainer?.getElementsByClassName(
      'row-drag-target-crosshair'
    )[0] as HTMLDivElement | undefined;

    if (!element || !tr || !crosshairContainer || !crosshair) {
      if (
        // Only print this error once:
        rowPathIndexes.length === 1 &&
        rowPathIndexes[0] === 0
      ) {
        console.error(
          'Cannot initialize drag handle: cannot find required HTML elements'
        );
      }
      return;
    }

    if (isFiltered) {
      element.draggable = false;
      return;
    }

    tr.dataset.depth = rowDepthIndex.toString();

    const handleDragStart = (event: DragEvent) => {
      event.dataTransfer!.dropEffect = 'move';
      event.dataTransfer!.setDragImage(tr, 0, 0);

      tr.classList.add('is-dragged');
      Object.assign(crosshair.dataset, {
        fromPath: serializePath(rowPathIndexes),
        fromParentId: row.parentId,
        fromRowId: row.id,
      });
    };

    const handleDragEnd = (event: DragEvent) => {
      tr.classList.remove('is-dragged');
      crosshair.classList.remove('is-visible');
      crosshair.style.left = '0px';
      crosshair.style.top = '0px';

      const wasCancelled = event.dataTransfer!.dropEffect === 'none';
      if (wasCancelled) {
        return;
      }

      const { fromPath, toPath, fromRowId, fromParentId, toParentId } =
        crosshair.dataset || {};

      if (fromPath && toPath && toPath !== fromPath) {
        const deserializedFromPath = deserializePath(fromPath);
        const deserializedToPath = deserializePath(toPath);
        if (
          fromPath.length !== toPath.length ||
          fromPath[fromPath.length - 1] + 1 !== toPath[toPath.length - 1]
        ) {
          const sanitizedToParentId =
            toParentId && toParentId !== 'undefined'
              ? (toParentId as UUIDType)
              : null;
          const toDepthIndex = sanitizedToParentId
            ? (table.getRow(sanitizedToParentId)?.depth || 0) + 1
            : 0;

          onChangePosition?.(
            deserializedFromPath,
            deserializedToPath,
            (fromRowId || '') as UUIDType,
            fromParentId && fromParentId !== 'undefined'
              ? (fromParentId as UUIDType)
              : null,
            sanitizedToParentId,
            toDepthIndex
          );
          if (toParentId && toParentId !== 'undefined') {
            table.getRow(toParentId)?.toggleExpanded(true);
          }
        }
      }
    };

    const handleDragEnter = (event: DragEvent) => {
      crosshair.classList.add('is-visible');

      if (tryTopmostPosition(event)) {
        return;
      }

      const { fromPath, fromParentId, fromRowId } = crosshair.dataset || {};

      if (!fromPath || !fromRowId) {
        return;
      }

      const targetX = event.clientX - tr.getBoundingClientRect().left;
      const isTargetingDeeper = Boolean(
        targetX >
          crosshairOffset + (rowDepthIndex + 1) * LEVEL_TABULATION_WIDTH_PX
      );
      const isRowVisuallyExpanded =
        row.getIsExpanded() && Boolean(row.subRows.length);

      if (!isTargetingDeeper && isRowVisuallyExpanded) {
        return;
      }

      const rowPath = serializePath(rowPathIndexes);
      const isTargetingOwnDescendant =
        fromPath === rowPath ? isTargetingDeeper : rowPath.startsWith(fromPath);

      if (isTargetingOwnDescendant) {
        return;
      }

      const highestExpandedParent = getHighestExpandedParent(row);
      const isTargetingHighestExpandedParentLevel = Boolean(
        highestExpandedParent &&
          targetX <
            crosshairOffset +
              (highestExpandedParent.depth + 1) * LEVEL_TABULATION_WIDTH_PX
      );
      const targetDepthIndex = isTargetingHighestExpandedParentLevel
        ? highestExpandedParent!.depth
        : rowDepthIndex + Number(isTargetingDeeper);

      const isWithinSameParent =
        !isTargetingDeeper && fromParentId === (rowParentId || 'undefined');

      let toPath = isTargetingHighestExpandedParentLevel
        ? nextRowPath(serializePath(getRowPathIndexes(highestExpandedParent!)))
        : (
            isWithinSameParent
              ? // Because dragging a row right before itself and after itself is the same position,
                // we need to increment the row index in case targeting after a sibling that sits after the dragged row.
                isTargetingAfterSibling(fromRowId, row, table)
              : !isTargetingDeeper
          )
        ? nextRowPath(rowPath)
        : rowPath;

      const toParentId = isTargetingHighestExpandedParentLevel
        ? highestExpandedParent!.parentId
        : isTargetingDeeper
        ? row.id
        : rowParentId;

      if (isTargetingDeeper) {
        toPath += `${delimiter}${isRowVisuallyExpanded ? 0 : rowChildrenCount}`;
      }

      const crosshairX =
        crosshairOffset + targetDepthIndex * LEVEL_TABULATION_WIDTH_PX;

      const crosshairY =
        tr.getBoundingClientRect().bottom -
        crosshairContainer.getBoundingClientRect().top;

      crosshair.style.left = `${crosshairX}px`;
      crosshair.style.top = `${crosshairY}px`;
      crosshair.dataset.toPath = toPath;
      crosshair.dataset.toParentId = toParentId;
    };

    const handleDragLeave = (event: DragEvent) => {
      tryTopmostPosition(event);
    };

    // Tries to position the crosshair at the topmost position of the table. Returns true if successful, false otherwise.
    const tryTopmostPosition = (event: DragEvent) => {
      if (rowPathIndexes.length === 1 && rowPathIndexes[0] === 0) {
        const { height, top } = tr.getBoundingClientRect();

        if (event.clientY - top < height * 0.5) {
          crosshair.style.left = `${crosshairOffset}px`;
          crosshair.style.top = '0px';
          crosshair.dataset.toPath = '0';

          return true;
        }
      }
      return false;
    };

    // This prevents the native drag image (event.dataTransfer!.setDragImage) from "flying" back to its original position upon release
    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
    };

    element.draggable = true;
    element.addEventListener('dragstart', handleDragStart);
    element.addEventListener('dragend', handleDragEnd);
    tr.addEventListener('dragenter', handleDragEnter);
    tr.addEventListener('dragleave', handleDragLeave);
    document.body.addEventListener('dragover', handleDragOver);

    return () => {
      element.removeEventListener('dragstart', handleDragStart);
      element.removeEventListener('dragend', handleDragEnd);
      tr.removeEventListener('dragenter', handleDragEnter);
      tr.removeEventListener('dragleave', handleDragLeave);
      document.body.removeEventListener('dragover', handleDragOver);
    };
  }, [table, row, onChangePosition, crosshairOffset, isFiltered]);

  return (
    <DragIcon
      ref={ref}
      size={'small'}
      id={`drag-sort-table-row-handle-${props.row.index}`}
      className="drag-sort-handle"
      isDisabled={isFiltered ? 'filtered' : false}
    />
  );
});

const isTargetingAfterSibling = (
  rowId: string,
  row: Row<any>,
  table: Table<any>
) => {
  const data: any[] = row.depth
    ? row.getParentRow()?.originalSubRows || []
    : table.options.data;

  for (const datum of data) {
    if (datum[ROW_UNIQUE_KEY_NAME] === rowId) {
      return false;
    }
    if (datum[ROW_UNIQUE_KEY_NAME] === row.id) {
      return true;
    }
  }
  return false;
};

const getHighestExpandedParent = (row: Row<any>) =>
  row.getParentRows().find((r) => r.getIsExpanded());

const nextRowPath = (path: string) => {
  const lastDelimiter = path.lastIndexOf(delimiter);
  return lastDelimiter === -1
    ? (Number(path) + 1).toString()
    : `${path.substring(0, lastDelimiter)}${delimiter}${
        Number(path.substring(lastDelimiter + 1)) + 1
      }`;
};

const delimiter = ',' as const;
const serializePath = (path: number[]) => path.join(delimiter);
const deserializePath = (path: string) => path.split(delimiter).map(Number);
